import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';

const PRIMARY_KEY = 'machine_id';

const toLabel = (value) => {
    if (value === 'machine_description') return 'Description';
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isTruncatedColumn = (column) => /description/i.test(String(column || ''));

const normalizeColumns = (rawColumns) => {
    const deduped = [];
    const seen = new Set();
    (rawColumns || []).forEach((column) => {
        const key = String(column || '').trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        deduped.push(key);
    });
    if (!seen.has(PRIMARY_KEY)) {
        deduped.unshift(PRIMARY_KEY);
        return deduped;
    }
    return [PRIMARY_KEY, ...deduped.filter((column) => column !== PRIMARY_KEY)];
};

const buildRowByColumns = (columns, source = {}, columnDetails = {}) => {
    const row = {};
    columns.forEach((column) => {
        const value = source[column];
        if (value === null || value === undefined) {
            row[column] = '';
            return;
        }
        if (columnDetails[column]?.input_type === 'json_csv' && typeof value !== 'string') {
            try {
                row[column] = JSON.stringify(value, null, 2);
                return;
            } catch (err) {
                // fall through to default string conversion below
            }
        }
        row[column] = String(value);
    });
    return row;
};

const normalizeColumnDetails = (rawColumnDetails) => {
    const normalized = {};
    Object.entries(rawColumnDetails || {}).forEach(([column, detail]) => {
        normalized[column] = {
            input_type: detail?.input_type || 'text',
            data_type: detail?.data_type || 'text',
            required: Boolean(detail?.required),
            nullable: Boolean(detail?.nullable),
            has_default: Boolean(detail?.has_default),
        };
    });
    return normalized;
};

const formatInputTypeLabel = (detail) => {
    const value = detail?.input_type || detail?.data_type || 'text';
    if (value === 'json_csv') return 'JSONB';
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const getColumnHint = (column, detail, isEditing) => {
    const parts = [];
    parts.push(detail?.required ? 'Required' : 'Optional');
    parts.push(`Type: ${formatInputTypeLabel(detail)}`);
    if (detail?.input_type === 'json_csv') {
        parts.push('Edit as a JSON array, e.g. ["Item one", "Item two, with a comma"]');
    }
    if (isEditing && column === PRIMARY_KEY) {
        parts.push('Cannot be changed after creation');
    }
    return parts.join(' • ');
};

const getInputMode = (detail) => {
    if (detail?.input_type === 'integer' || detail?.input_type === 'number') {
        return 'numeric';
    }
    return undefined;
};

const validateValueByType = (column, value, detail) => {
    const trimmedValue = String(value ?? '').trim();
    const label = toLabel(column);

    if (!trimmedValue) {
        if (detail?.required) {
            return `${label} is required`;
        }
        return '';
    }

    if (detail?.input_type === 'integer' && !/^[+-]?\d+$/.test(trimmedValue)) {
        return `${label} must be an integer`;
    }

    if (detail?.input_type === 'number' && Number.isNaN(Number(trimmedValue))) {
        return `${label} must be a number`;
    }

    if (detail?.input_type === 'boolean' && !/^(true|false|1|0|yes|no|y|n)$/i.test(trimmedValue)) {
        return `${label} must be true or false`;
    }

    if (detail?.input_type === 'json_csv') {
        try {
            JSON.parse(trimmedValue);
        } catch (err) {
            return `${label} must be valid JSON (e.g. an array of strings)`;
        }
    }

    return '';
};

const getNextMachineId = (rows) => {
    const maxMachineId = (rows || []).reduce((maxValue, row) => {
        const rawValue = row?.[PRIMARY_KEY];
        const numericValue = Number.parseInt(String(rawValue ?? '').trim(), 10);
        if (Number.isNaN(numericValue)) {
            return maxValue;
        }
        return Math.max(maxValue, numericValue);
    }, 0);
    return String(maxMachineId + 1);
};

const VLAAdminMachinesTable = () => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [columnDetails, setColumnDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingMachineId, setEditingMachineId] = useState(null);
    const [formRow, setFormRow] = useState({});
    const [saveError, setSaveError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const isEditing = editingMachineId !== null;
    const tableColumns = useMemo(() => {
        if (columns.length > 0) return columns;
        if (rows.length === 0) return [PRIMARY_KEY];
        return normalizeColumns(Object.keys(rows[0] || {}));
    }, [columns, rows]);

    const formColumns = useMemo(() => {
        if (!isEditing) {
            return tableColumns.filter((column) => column !== PRIMARY_KEY);
        }
        return tableColumns;
    }, [isEditing, tableColumns]);

    const fetchRows = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/vla-admin/sample-machines`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                }
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(getApiErrorMessage(data, 'Failed to fetch VLA machine data'));
            }
            const fetchedRows = Array.isArray(data.data) ? data.data : [];
            const metaColumns = normalizeColumns(data.meta?.columns || []);
            const metaColumnDetails = normalizeColumnDetails(data.meta?.column_details || {});
            const fallbackColumns = fetchedRows.length > 0
                ? normalizeColumns(Object.keys(fetchedRows[0] || {}))
                : [PRIMARY_KEY];
            setRows(fetchedRows);
            setColumns(metaColumns.length > 0 ? metaColumns : fallbackColumns);
            setColumnDetails(metaColumnDetails);
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError(err.message || 'Failed to fetch VLA machine data');
        } finally {
            setLoading(false);
        }
    }, [handleAdminSessionExpired]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const handleOpenAdd = () => {
        setEditingMachineId(null);
        setSaveError('');
        setFormRow({
            ...buildRowByColumns(formColumns),
            [PRIMARY_KEY]: getNextMachineId(rows),
        });
        setShowModal(true);
    };

    const handleOpenEdit = (row) => {
        setEditingMachineId(row?.[PRIMARY_KEY] ?? null);
        setSaveError('');
        setFormRow(buildRowByColumns(tableColumns, row || {}, columnDetails));
        setShowModal(true);
    };

    const handleCloseModal = () => {
        if (isSaving) return;
        setShowModal(false);
        setEditingMachineId(null);
        setFormRow({});
        setSaveError('');
    };

    const handleInputChange = (column, value) => {
        setFormRow((prev) => ({
            ...prev,
            [column]: value,
        }));
    };

    const handleSubmit = async () => {
        setSaveError('');
        const machineId = (formRow[PRIMARY_KEY] || '').trim();
        if (isEditing && !machineId) {
            setSaveError('machine_id is required');
            return;
        }

        const rowPayload = {};
        formColumns.forEach((column) => {
            rowPayload[column] = formRow[column] ?? '';
        });
        if (isEditing) {
            rowPayload[PRIMARY_KEY] = editingMachineId;
        } else {
            rowPayload[PRIMARY_KEY] = formRow[PRIMARY_KEY] ?? '';
        }

        for (const column of formColumns) {
            const validationError = validateValueByType(column, formRow[column] ?? '', columnDetails[column]);
            if (validationError) {
                setSaveError(validationError);
                return;
            }
        }

        try {
            setIsSaving(true);
            const token = localStorage.getItem("prismax_admin_token");
            const payload = {row: rowPayload};
            if (isEditing) {
                payload.original_machine_id = editingMachineId;
            }
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/vla-admin/sample-machines`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(getApiErrorMessage(data, 'Failed to save row'));
            }
            handleCloseModal();
            await fetchRows();
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setSaveError(err.message || 'Failed to save row');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading VLA machine data...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>VLA Admin - Sample Machines</h2>
                <button className={styles.addButton} onClick={handleOpenAdd}>
                    Add Row
                </button>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        {tableColumns.map((column) => (
                            <th
                                key={column}
                                className={isTruncatedColumn(column) ? styles.compactColumn : undefined}
                                title={toLabel(column)}
                            >
                                {toLabel(column)}
                            </th>
                        ))}
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.length > 0 ? rows.map((row, index) => (
                        <tr key={`${row[PRIMARY_KEY] ?? 'row'}-${index}`}>
                            {tableColumns.map((column) => {
                                const value = row[column];
                                const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
                                const shouldTruncate = isTruncatedColumn(column);
                                return (
                                    <td
                                        key={`${row[PRIMARY_KEY] ?? index}-${column}`}
                                        className={shouldTruncate ? styles.compactColumn : undefined}
                                    >
                                        <span
                                            className={shouldTruncate ? styles.truncatedCellText : undefined}
                                            title={displayValue}
                                        >
                                            {displayValue}
                                        </span>
                                    </td>
                                );
                            })}
                            <td>
                                <button
                                    type="button"
                                    className={styles.editButton}
                                    onClick={() => handleOpenEdit(row)}
                                >
                                    Edit
                                </button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={tableColumns.length + 1} className={styles.noData}>
                                No data found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>{isEditing ? 'Edit Row' : 'Add Row'}</h3>
                        <p className={styles.formIntro}>
                            Check the hint under each field for whether it is required and what value type it expects.
                        </p>
                        {!isEditing && (
                            <div className={styles.fieldHint}>
                                Machine ID is auto-generated when the row is created.
                            </div>
                        )}

                        {formColumns.map((column) => (
                            <div className={styles.formGroup} key={column}>
                                <label>{toLabel(column)}:</label>
                                {columnDetails[column]?.input_type === 'json_csv' ? (
                                    <textarea
                                        value={formRow[column] ?? ''}
                                        onChange={(event) => handleInputChange(column, event.target.value)}
                                        placeholder={getColumnHint(column, columnDetails[column], isEditing)}
                                        className={`${styles.input} ${styles.textarea}`}
                                        disabled={isEditing && column === PRIMARY_KEY}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={formRow[column] ?? ''}
                                        onChange={(event) => handleInputChange(column, event.target.value)}
                                        placeholder={getColumnHint(column, columnDetails[column], isEditing)}
                                        className={styles.input}
                                        inputMode={getInputMode(columnDetails[column])}
                                        disabled={isEditing && column === PRIMARY_KEY}
                                    />
                                )}
                                <div className={styles.fieldHint}>
                                    {getColumnHint(column, columnDetails[column], isEditing)}
                                </div>
                            </div>
                        ))}

                        {saveError && <div className={styles.errorMessage}>{saveError}</div>}

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={handleCloseModal}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VLAAdminMachinesTable;
